/* Playo — Channels list + create/edit drawer.
   - Two kinds of channel: "Schedule" (24/7 from library videos, filler in
     gaps) and "External stream" (an existing live .m3u8 the platform plays
     as-is; the schedule only holds guide entries). External URLs can be
     checked before saving; the backend re-checks them every 2 minutes.
   - Gated by the Live TV permission module (livetv: view/create/edit/delete);
     the backend enforces the same (requirePermission on /api/v1/playo).
   - Filler asset shows its channel-ready status; setting a non-ready filler
     queues its transcode (the API warns, we toast the warning). */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import um from "../userManagement/userManagement.module.css";
import s from "./Playo.module.css";
import { useRbac } from "../../rbac/RbacContext";
import { showToast } from "../../utils/Toastify/Toastify";
import { listChannels, saveChannel, deleteChannel, testSource, apiErrorMessage } from "./playoService";
import { LibraryPicker, ChannelReadyStatus, TIMEZONES } from "./PlayoShared";

const EMPTY = { name: "", slug: "", logo: "", status: "draft", timezone: "UTC", filler_asset_id: null, filler_name: "", source_type: "schedule", source_url: "" };

const isExternal = (ch) => ch?.source_type === "external";

/** "Online" / "Offline" / "Not checked" pill for an external channel. */
export function SourceStatus({ channel }) {
  if (!isExternal(channel)) return null;
  const st = channel.source_status;
  const cls = st === "online" ? s.pill_ok : st === "offline" ? s.pill_bad : s.pill_muted;
  const label = st === "online" ? "Online" : st === "offline" ? "Offline" : "Not checked";
  return (
    <span className={`${s.pill} ${cls}`} title={channel.source_error || undefined} data-testid="source-status">
      {label}
    </span>
  );
}

/** Result line of a "Test stream" check. */
export function ProbeResult({ result }) {
  if (!result) return null;
  if (result.ok) {
    const what = result.kind === "master" ? `${result.variants} quality level${result.variants === 1 ? "" : "s"}` : "single quality";
    return (
      <span className={s.okText} data-testid="probe-result">
        ✓ Live stream found ({what}).
      </span>
    );
  }
  return (
    <span className={um.errorText} data-testid="probe-result">
      {result.error || "The stream didn't answer."}
    </span>
  );
}

export function ChannelForm({ initial, onSaved, onCancel, canEdit }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...initial,
    logo: initial?.logo || "",
    slug: initial?.slug || "",
    source_type: initial?.source_type || "schedule",
    source_url: initial?.source_url || "",
  }));
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const external = form.source_type === "external";
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(null); // null | "image" (logo) | "video" (filler)
  const isEdit = Boolean(initial?.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (form.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) errs.slug = "Lowercase letters, numbers and single dashes only";
    if (form.logo && !/^https?:\/\//i.test(form.logo)) errs.logo = "Logo must be an http(s) URL";
    if (external && !/^https?:\/\/\S+$/i.test(form.source_url.trim())) errs.source_url = "Paste the stream's http(s) .m3u8 URL";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        logo: form.logo.trim() || null,
        status: form.status,
        timezone: form.timezone,
        source_type: form.source_type,
      };
      if (external) body.source_url = form.source_url.trim();
      else body.filler_asset_id = form.filler_asset_id || null;
      if (form.slug) body.slug = form.slug;
      const { channel, message } = await saveChannel(initial?.id, body);
      showToast(message || "Channel saved", /warning/i.test(message) ? "warning" : "success");
      onSaved(channel);
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") setErrors(fieldErrors);
      showToast(apiErrorMessage(err, "Could not save the channel"), "error");
    } finally {
      setSaving(false);
    }
  };

  const runProbe = async () => {
    const url = form.source_url.trim();
    if (!/^https?:\/\/\S+$/i.test(url)) {
      setErrors((e) => ({ ...e, source_url: "Paste the stream's http(s) .m3u8 URL" }));
      return;
    }
    setProbing(true);
    setProbe(null);
    try {
      setProbe(await testSource(url, isEdit && initial?.source_url === url ? initial.id : null));
      setErrors((e) => ({ ...e, source_url: undefined }));
    } catch (err) {
      const fe = err?.response?.data?.errors;
      setProbe({ ok: false, error: fe?.url || apiErrorMessage(err, "Could not check the stream") });
    } finally {
      setProbing(false);
    }
  };

  return (
    <form onSubmit={submit} className={um.drawerBody} aria-label={isEdit ? "Edit channel" : "New channel"}>
      <div className={um.field} role="radiogroup" aria-label="Channel source">
        <span className={um.label}>Source</span>
        <div className={s.sourceChoice}>
          <label className={`${s.sourceOption} ${!external ? s.sourceOptionOn : ""}`}>
            <input type="radio" name="source_type" value="schedule" checked={!external} onChange={set("source_type")} disabled={!canEdit} />
            <span>
              <span className={s.sourceTitle}>Schedule from my library</span>
              <span className={s.muted}>A 24/7 channel built from your videos, with filler in the gaps.</span>
            </span>
          </label>
          <label className={`${s.sourceOption} ${external ? s.sourceOptionOn : ""}`}>
            <input type="radio" name="source_type" value="external" checked={external} onChange={set("source_type")} disabled={!canEdit} />
            <span>
              <span className={s.sourceTitle}>External live stream</span>
              <span className={s.muted}>Play an existing live HLS stream (.m3u8) you have the rights to.</span>
            </span>
          </label>
        </div>
      </div>
      {external && (
        <div className={um.field}>
          <span className={um.label}>Stream URL (.m3u8) *</span>
          <div className={s.urlInputRow}>
            <input
              className={um.input}
              value={form.source_url}
              onChange={(e) => {
                set("source_url")(e);
                setProbe(null);
              }}
              placeholder="https://example.com/live/playlist.m3u8"
              disabled={!canEdit}
              aria-label="Stream URL"
            />
            <button type="button" className={um.btnSubtle} onClick={runProbe} disabled={probing || !form.source_url.trim()}>
              {probing ? "Checking…" : "Test stream"}
            </button>
          </div>
          <ProbeResult result={probe} />
          {!probe && isEdit && isExternal(initial) && initial.source_url === form.source_url.trim() && initial.source_status && (
            <span className={s.muted}>
              Last check: <SourceStatus channel={initial} />
              {initial.source_error ? ` ${initial.source_error}` : ""}
            </span>
          )}
          <span className={um.hint}>
            Viewers keep using this channel's own live URL; it forwards to the stream. The stream must allow browser playback (CORS) to play on the website.
          </span>
          {errors.source_url && <span className={um.errorText}>{errors.source_url}</span>}
        </div>
      )}
      <label className={um.field}>
        <span className={um.label}>Name *</span>
        <input className={um.input} value={form.name} onChange={set("name")} disabled={!canEdit} aria-label="Name" />
        {errors.name && <span className={um.errorText}>{errors.name}</span>}
      </label>
      <label className={um.field}>
        <span className={um.label}>Slug</span>
        <input className={um.input} value={form.slug} onChange={set("slug")} placeholder="auto from name" disabled={!canEdit} aria-label="Slug" />
        <span className={um.hint}>Used in the channel's live URL. Changing it changes the URL.</span>
        {errors.slug && <span className={um.errorText}>{errors.slug}</span>}
      </label>
      <div className={um.field}>
        <span className={um.label}>Logo</span>
        <div className={s.logoRow}>
          {form.logo && /^https?:\/\//i.test(form.logo) ? (
            <img src={form.logo} alt="Channel logo" className={s.logoPreview} />
          ) : (
            <span className={s.muted}>No logo</span>
          )}
          {canEdit && (
            <button type="button" className={um.btnSubtle} onClick={() => setPicking("image")}>
              {form.logo ? "Change logo" : "Choose from Media Library"}
            </button>
          )}
          {canEdit && form.logo && (
            <button type="button" className={um.linkBtn} onClick={() => setForm((f) => ({ ...f, logo: "" }))}>
              Remove
            </button>
          )}
        </div>
        <span className={um.hint}>Pick an image from the Media Library, or upload a new one there.</span>
        {errors.logo && <span className={um.errorText}>{errors.logo}</span>}
      </div>
      <div className={um.fieldRow}>
        <label className={um.field}>
          <span className={um.label}>Status</span>
          <select className={um.select} value={form.status} onChange={set("status")} disabled={!canEdit} aria-label="Status">
            <option value="draft">Draft</option>
            <option value="active">Active (public)</option>
          </select>
          {errors.status && <span className={um.errorText}>{errors.status}</span>}
        </label>
        <label className={um.field}>
          <span className={um.label}>Timezone</span>
          <select className={um.select} value={form.timezone} onChange={set("timezone")} disabled={!canEdit} aria-label="Timezone">
            {(TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES]).map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <span className={um.hint}>The scheduler shows times in this zone. They're stored as UTC.</span>
        </label>
      </div>
      {!external && (
      <div className={um.field}>
        <span className={um.label}>Filler asset</span>
        <div className={s.readyRow}>
          <span>{form.filler_asset_id ? form.filler_name || `Video #${form.filler_asset_id}` : <span className={s.muted}>None</span>}</span>
          {canEdit && (
            <button type="button" className={um.btnSubtle} onClick={() => setPicking("video")}>
              {form.filler_asset_id ? "Change" : "Choose filler"}
            </button>
          )}
          {canEdit && form.filler_asset_id && (
            <button type="button" className={um.linkBtn} onClick={() => setForm((f) => ({ ...f, filler_asset_id: null, filler_name: "" }))}>
              Remove
            </button>
          )}
        </div>
        {form.filler_asset_id && <ChannelReadyStatus videoId={form.filler_asset_id} canEdit={canEdit} />}
        <span className={um.hint}>
          A video from the Media Library (you can upload one there). Loops in every gap of the schedule, and whenever an item's asset isn't channel-ready.
        </span>
        {form.status === "active" && !form.filler_asset_id && (
          <span className={s.warnText}>Without a filler, the live stream pauses in every gap.</span>
        )}
        {errors.filler_asset_id && <span className={um.errorText}>{errors.filler_asset_id}</span>}
      </div>
      )}
      <div className={um.drawerFooter}>
        <button type="button" className={um.btnGhost} onClick={onCancel}>
          Cancel
        </button>
        {canEdit && (
          <button type="submit" className={um.btnPrimary} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create channel"}
          </button>
        )}
      </div>
      <LibraryPicker
        open={Boolean(picking)}
        kind={picking}
        onClose={() => setPicking(null)}
        onPick={(item) =>
          picking === "image"
            ? setForm((f) => ({ ...f, logo: item.bunny_cdn_url || item.url || f.logo }))
            : setForm((f) => ({ ...f, filler_asset_id: item.id, filler_name: item.title || item.name || "" }))
        }
      />
    </form>
  );
}

const PlayoChannels = () => {
  const { can } = useRbac();
  const canView = can("livetv", "view");
  const canCreate = can("livetv", "create");
  const canEdit = can("livetv", "edit");
  const canDelete = can("livetv", "delete");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | channel
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listChannels({ q, status }));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load channels"));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    if (canView) load();
  }, [load, canView]);

  if (!canView) {
    return (
      <div className={um.page}>
        <div className={um.emptyState}>
          <div className={um.emptyTitle}>No access</div>
          <div className={um.emptyMessage}>You don't have permission to view channels.</div>
        </div>
      </div>
    );
  }

  const remove = async () => {
    const ch = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteChannel(ch.id);
      showToast("Channel deleted", "success");
      load();
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not delete the channel"), "error");
    }
  };

  return (
    <div className={um.page}>
      <div className={um.header}>
        <div className={um.headerTitleWrap}>
          <h1 className={um.title}>Channels</h1>
          <div className={um.subtitle}>24/7 linear &amp; FAST channels: scheduled from your library, or an external live stream.</div>
        </div>
        <div className={um.headerActions}>
          {canCreate && (
            <button type="button" className={um.btnPrimary} onClick={() => setEditing({})}>
              New channel
            </button>
          )}
        </div>
      </div>

      <div className={um.toolbar}>
        <input className={um.input} placeholder="Search channels…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search channels" />
        <select className={um.filterSelect} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className={um.tableCard}>
        {loading ? (
          <div className={s.muted} style={{ padding: 16 }}>
            Loading…
          </div>
        ) : error ? (
          <div className={um.errorText} style={{ padding: 16 }}>
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className={um.emptyState}>
            <div className={um.emptyTitle}>No channels yet</div>
            <div className={um.emptyMessage}>Create a channel from your library (pick a filler, then build its schedule), or paste an external live stream URL.</div>
          </div>
        ) : (
          <div className={um.tableScroll}>
            <table className={um.table}>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Timezone</th>
                  <th>Source</th>
                  <th className={um.colActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ch) => (
                  <tr key={ch.id}>
                    <td>
                      <div className={s.channelCell}>
                        {ch.logo ? <img src={ch.logo} alt="" className={s.logoPreview} /> : <span className={s.logoPreview} />}
                        <div>
                          <div className={s.channelName}>{ch.name}</div>
                          <div className={s.muted}>{ch.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${s.pill} ${ch.status === "active" ? s.pill_ok : s.pill_muted}`}>{ch.status === "active" ? "Active" : "Draft"}</span>
                    </td>
                    <td>{ch.timezone}</td>
                    <td>
                      {isExternal(ch) ? (
                        <span className={s.readyRow}>
                          External stream <SourceStatus channel={ch} />
                        </span>
                      ) : ch.filler_asset_id ? (
                        <ChannelReadyStatus videoId={ch.filler_asset_id} canEdit={false} />
                      ) : (
                        <span className={s.pill_warnText}>No filler</span>
                      )}
                    </td>
                    <td className={um.colActions}>
                      <div className={um.rowActions}>
                        <Link className={um.btnSubtle} to={`/mainlayout/playo/channels/${ch.id}`}>
                          {isExternal(ch) ? "Guide" : "Schedule"}
                        </Link>
                        <button type="button" className={um.btnGhost} onClick={() => setEditing(ch)}>
                          {canEdit ? "Edit" : "View"}
                        </button>
                        {canDelete && (
                          <button type="button" className={um.btnDanger} onClick={() => setConfirmDelete(ch)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className={um.modalOverlay} onClick={() => setEditing(null)}>
          <aside className={um.drawer} onClick={(e) => e.stopPropagation()}>
            <div className={um.drawerHeader}>
              <div className={um.drawerTitle}>{editing.id ? `Edit ${editing.name}` : "New channel"}</div>
            </div>
            <ChannelForm
              initial={editing}
              canEdit={editing.id ? canEdit : canCreate}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          </aside>
        </div>
      )}

      {confirmDelete && (
        <div className={um.dialogOverlay} role="dialog" aria-modal="true">
          <div className={um.dialog}>
            <h3 className={um.dialogTitle}>Delete {confirmDelete.name}?</h3>
            <p className={um.dialogMessage}>This also deletes its whole schedule. It can't be undone.</p>
            <div className={um.dialogActions}>
              <button type="button" className={um.btnGhost} onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button type="button" className={um.btnDanger} onClick={remove}>
                Delete channel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayoChannels;
